import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addDays, diffDays, xDias, anchoDias } from './constants';
import { CATEGORIA_COLOR } from '../utils/constants';
import { dateKey } from '../utils/helpers';

const CLICK_THRESHOLD_PX = 4;

// Mientras se arrastra, la barra se desliza de dia en dia en vez de saltar.
// Solo durante el arrastre: el resto del tiempo sigue exacta al zoom.
const DESLIZAR = 'left 140ms cubic-bezier(0.22, 1, 0.36, 1), width 140ms cubic-bezier(0.22, 1, 0.36, 1)';

const fechaCorta = (d) => (d ? d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' }) : '');

// Fechas que quedarian al soltar con `deltaDays` en el modo de arrastre dado.
const fechasConDelta = (mode, deltaDays, { fechaInicio, fechaFin, start, end }) => {
  let nuevoStart = fechaInicio;
  let nuevoEnd = fechaFin;
  if (mode === 'move') {
    if (fechaInicio) nuevoStart = addDays(fechaInicio, deltaDays);
    if (fechaFin) nuevoEnd = addDays(fechaFin, deltaDays);
  } else if (mode === 'resize-left') {
    nuevoStart = addDays(start, deltaDays);
    if (!nuevoEnd) nuevoEnd = end;
    if (nuevoEnd && nuevoStart > nuevoEnd) nuevoStart = nuevoEnd;
  } else if (mode === 'resize-right') {
    nuevoEnd = addDays(end, deltaDays);
    if (!nuevoStart) nuevoStart = start;
    if (nuevoStart && nuevoEnd < nuevoStart) nuevoEnd = nuevoStart;
  }
  return { nuevoStart, nuevoEnd };
};

/**
 * Barra de Gantt para una tarea con interacciones:
 *  - mouseDown en el centro + arrastrar → mueve la barra completa (preserva duración)
 *  - mouseDown en handle izquierdo → cambia fecha_inicio
 *  - mouseDown en handle derecho → cambia fecha_vencimiento
 *  - click sin arrastrar → onClick(tarea)
 *
 * onUpdate(tareaId, { fecha_inicio, fecha_vencimiento }) se llama al soltar el mouse
 * si hubo cambio.
 *
 * Además pinta:
 *  - progreso: relleno más intenso proporcional a subtareas hechas (100% si
 *    la tarea está hecha);
 *  - rayado diagonal si la tarea está congelada/bloqueada (espera a una
 *    predecesora sin cerrar);
 *  - contorno naranja si está en la ruta crítica (`critica`);
 *  - el título afuera, a la derecha, cuando la barra es muy angosta para leerlo.
 */
// Color de la ruta crítica (naranja: se distingue del rose de "vencido").
export const COLOR_CRITICO = '#ea580c';

const TaskBar = ({ tarea, dayWidth, firstDay, onClick, onUpdate, critica = false }) => {
  const [dragMode, setDragMode] = useState(null); // null | 'move' | 'resize-left' | 'resize-right'
  const [previewDelta, setPreviewDelta] = useState(0);
  const dragStartX = useRef(0);
  const didDrag = useRef(false);

  const fechaInicio = tarea.fecha_inicio ? new Date(tarea.fecha_inicio + 'T00:00:00') : null;
  const fechaFin = tarea.fecha_vencimiento ? new Date(tarea.fecha_vencimiento + 'T00:00:00') : null;
  const start = fechaInicio || fechaFin;
  const end = fechaFin || fechaInicio;

  // Listeners de mousemove/mouseup en window mientras se está arrastrando.
  // Va ANTES del early return (regla de hooks: no llamarlos condicionalmente).
  useEffect(() => {
    if (!dragMode || !start) return;

    const onMove = (e) => {
      const deltaX = e.clientX - dragStartX.current;
      if (Math.abs(deltaX) > CLICK_THRESHOLD_PX) didDrag.current = true;
      const deltaDays = Math.round(deltaX / dayWidth);
      setPreviewDelta(deltaDays);
    };

    const onUp = (e) => {
      const deltaX = e.clientX - dragStartX.current;
      const deltaDays = Math.round(deltaX / dayWidth);
      const mode = dragMode;
      setDragMode(null);
      setPreviewDelta(0);

      if (deltaDays === 0) return;

      const { nuevoStart, nuevoEnd } = fechasConDelta(mode, deltaDays, { fechaInicio, fechaFin, start, end });

      onUpdate?.(tarea.id, {
        fecha_inicio: nuevoStart ? dateKey(nuevoStart) : null,
        fecha_vencimiento: nuevoEnd ? dateKey(nuevoEnd) : null,
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragMode, dayWidth]);

  if (!start) return null;

  const offsetDays = diffDays(start, firstDay);
  const duration = Math.max(1, diffDays(end, start) + 1);

  const cat = tarea.estado?.categoria || 'por_hacer';
  const estadoColor = tarea.estado?.color || CATEGORIA_COLOR[cat] || '#94a3b8';
  const isDone = cat === 'hecho' || cat === 'cancelado';
  const isMilestone = !!fechaFin && !fechaInicio;

  // Visual con preview del drag aplicado.
  let visualLeftDays = offsetDays;
  let visualWidthDays = duration;
  if (dragMode === 'move') {
    visualLeftDays += previewDelta;
  } else if (dragMode === 'resize-left') {
    visualLeftDays += previewDelta;
    visualWidthDays -= previewDelta;
    if (visualWidthDays < 1) {
      visualLeftDays = offsetDays + duration - 1;
      visualWidthDays = 1;
    }
  } else if (dragMode === 'resize-right') {
    visualWidthDays += previewDelta;
    if (visualWidthDays < 1) visualWidthDays = 1;
  }

  // Posicion y ancho van con --dw (siguen a la camara sin renders); el ancho en
  // px solo decide si el titulo entra en la barra.
  const anchoPx = Math.max(8, visualWidthDays * dayWidth - 4);
  const congelada = !isDone && (tarea.congelada ?? tarea.esta_bloqueada);
  const progreso = cat === 'hecho'
    ? 100
    : (tarea.subtareas_total > 0 ? Math.round((tarea.subtareas_completadas / tarea.subtareas_total) * 100) : 0);
  const etiquetaAfuera = anchoPx < 72;
  const esperaA = (tarea.bloqueadoras_pendientes || []).map((b) => b.codigo).join(', ');
  const tooltip = [
    `${tarea.codigo} · ${tarea.titulo}`,
    progreso > 0 && cat !== 'hecho' ? `${progreso}% de subtareas` : null,
    congelada ? `Congelada: espera a ${esperaA || 'una predecesora'}` : null,
    critica ? 'En la ruta crítica' : null,
  ].filter(Boolean).join('\n');

  const startDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    dragStartX.current = e.clientX;
    didDrag.current = false;
    setDragMode(mode);
    setPreviewDelta(0);
  };

  const handleBodyClick = (e) => {
    if (didDrag.current) {
      // hubo drag → no abrir slide-over
      didDrag.current = false;
      return;
    }
    e.stopPropagation();
    onClick?.(tarea);
  };

  // Fechas que quedarian al soltar, a la vista mientras se arrastra: en Mes o
  // Trimestre un dia mide pocos pixeles y no se puede leer en la grilla.
  let textoArrastre = null;
  if (dragMode && previewDelta !== 0) {
    const { nuevoStart, nuevoEnd } = fechasConDelta(dragMode, previewDelta, { fechaInicio, fechaFin, start, end });
    if (dragMode === 'resize-left') textoArrastre = `Inicio ${fechaCorta(nuevoStart)}`;
    else if (dragMode === 'resize-right') textoArrastre = `Vence ${fechaCorta(nuevoEnd)}`;
    else if (nuevoStart && nuevoEnd && nuevoStart.getTime() !== nuevoEnd.getTime()) {
      textoArrastre = `${fechaCorta(nuevoStart)} – ${fechaCorta(nuevoEnd)}`;
    } else {
      textoArrastre = fechaCorta(nuevoStart || nuevoEnd);
    }
  }

  return (
    <div
      className={`absolute top-1.5 h-[24px] rounded-md transition-shadow hover:shadow-md hover:ring-1 hover:ring-slate-900/15 ${
        isDone ? 'opacity-60' : ''
      } ${isMilestone ? 'rounded-sm' : ''} ${dragMode ? 'shadow-lg ring-1 ring-slate-900/15' : ''}`}
      style={{
        left: xDias(visualLeftDays),
        width: anchoDias(visualWidthDays, 4, 8),
        backgroundColor: `${estadoColor}26`,
        backgroundImage: congelada
          ? `repeating-linear-gradient(135deg, ${estadoColor}33 0 4px, transparent 4px 8px)`
          : undefined,
        border: `1px ${congelada ? 'dashed' : 'solid'} ${estadoColor}${congelada ? '80' : '40'}`,
        boxShadow: critica ? `0 0 0 1.5px ${COLOR_CRITICO}` : undefined,
        transition: dragMode ? `${DESLIZAR}, box-shadow 150ms` : undefined,
        zIndex: dragMode ? 20 : undefined,
      }}
    >
      <AnimatePresence>
        {textoArrastre && (
          <motion.span
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 2, transition: { duration: 0.12 } }}
            transition={{ type: 'spring', stiffness: 520, damping: 32 }}
            className={`absolute bottom-full mb-1.5 px-1.5 py-0.5 rounded bg-slate-900 text-white text-[10px] font-medium tabular-nums whitespace-nowrap pointer-events-none shadow-sm ${
              dragMode === 'resize-right' ? 'right-0' : 'left-0'
            }`}
          >
            {textoArrastre}
          </motion.span>
        )}
      </AnimatePresence>
      {/* Progreso: relleno más intenso desde la izquierda */}
      {progreso > 0 && (
        <div
          className="absolute left-0 top-0 bottom-0 rounded-l-md pointer-events-none"
          style={{
            width: `${progreso}%`,
            backgroundColor: `${estadoColor}33`,
            borderRadius: progreso >= 100 ? '0.375rem' : undefined,
          }}
        />
      )}

      {/* Handle izquierdo (resize-left) */}
      <div
        onMouseDown={(e) => startDrag(e, 'resize-left')}
        title="Cambiar inicio"
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/10 rounded-l-md transition-colors z-[1]"
      />

      {/* Cuerpo: drag para mover + click para abrir */}
      <button
        type="button"
        onMouseDown={(e) => startDrag(e, 'move')}
        onClick={handleBodyClick}
        title={tooltip}
        className={`relative flex items-center w-full h-full px-3 text-[11px] font-medium text-left truncate ${
          dragMode === 'move' ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        style={{ color: estadoColor }}
      >
        {!etiquetaAfuera && <span className="truncate pointer-events-none">{tarea.titulo}</span>}
      </button>

      {/* Handle derecho (resize-right) */}
      <div
        onMouseDown={(e) => startDrag(e, 'resize-right')}
        title="Cambiar vencimiento"
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/10 rounded-r-md transition-colors z-[1]"
      />

      {/* Barra angosta: título afuera, a la derecha */}
      {etiquetaAfuera && (
        <span
          className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 max-w-[240px] truncate text-[11px] text-slate-600 whitespace-nowrap pointer-events-none"
        >
          {tarea.titulo}
        </span>
      )}
    </div>
  );
};

export default TaskBar;
